import { theme } from "shared/src/theme";
import { styled } from "styled-components";
import React, { useState } from "react";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { SupportCard } from "../../components/molecules/SupportCard";
import { ArticleList } from "../../components/organisms/ArticleList";
import { CalenderList } from "../../components/organisms/CalenderList";
import { LinkCardList } from "../../components/organisms/LinkList";
import { NoticeCarousel } from "../../components/organisms/NoticeCarousel";
import Head from "next/head";
import { useRouter } from "next/router";
import { Result } from "antd";
import { PREFECTURE_OPTIONS } from "shared";
import dynamic from "next/dynamic";
import { GetServerSideProps } from "next";
import {
  FetchTag,
  GetArticleList,
  GetFeaturedArticle,
} from "../../service/article";
import { GetTopEventsList } from "../../service/event";
import { GetLinkList } from "../../service/link";
import { CityCard } from "../../components/molecules/CityCard";
import { TopCarousel } from "../../components";
import { GetNewsList } from "../../service/news";
import { CITY_META_DATA } from "../../utils/metadata";

const DynamicCityImageWithNoSSR = dynamic(
  () => import("../../components/atoms/CityImage"),
  { ssr: false }
);

const ErrorContainer = styled.div`
  margin-top: 120px;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0px;
  margin-top: 134px;
  @media (max-width: 1550px) {
    padding: 0px 5% 0px 5%;
  }
  @media (max-width: 1440px) {
    padding: 0px 9.02% 0px 8.33%;
  }
  @media (max-width: 1245px) {
    padding: 0px 5%;
  }
`;
const CityTypography = styled.div`
  font-family: Open Sans;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.gray2};
  margin-top: 64px;
  margin-bottom: 12px;
`;

const TitleTypography = styled.h1`
  font-family: Open Sans;
  font-style: normal;
  font-weight: 600;
  font-size: 34px;
  line-height: 40px;
  color: ${theme.black};
  margin-bottom: 32px;
  width: fit-content;
  border-bottom: 3px solid ${theme.buttonRed};
  padding-bottom: 16px;
`;
const ContentWrapper = styled.p`
  font-family: Open Sans;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.black};
  margin-bottom: 45px;
  white-space: break-spaces;
  letter-spacing: 0.0015em;
  @media (max-width: 700px) {
    margin-bottom: 24px;
  }
`;
const ImageWrapper = styled.div`
  max-width: 1440px;
  position: relative;
  margin-bottom: 64px;
  & .img-size {
    max-width: 1440px;
    max-height: 480px;
    height: 100%;
    width: 100%;
    padding-right: 16px;

    @media (max-width: 800px) {
      width: 100%;
      padding-right: 8px;
    }
  }
`;
const ImageBackground = styled.div`
  background-color: ${theme.buttonRed};
  width: 47%;
  height: 100%;
  position: absolute;
  top: 16px;
  z-index: -1;
  right: 0px;
  @media (max-width: 700px) {
    width: 47%;

    top: 8px;
  }
`;

const slugIdMap: { [key: string]: string } = {
  osaka: "1",
  sakai: "2",
  hyogo_kobe: "3",
  kyoto: "4",
  shiga: "5",
  wakayama: "6",
  tokushima: "7",
  tottori: "8",
  nara: "9",
};

const City = (props) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const id: any = slugIdMap[router.query.id as string];

  if (!id || id > 9 || id == 0) {
    return (
      <ErrorContainer>
        <Result
          status="404"
          title="404"
          subTitle={t("Sorry, the page you visited does not exist.")}
        />
      </ErrorContainer>
    );
  }
  const GetPrefecture = (code) => {
    const name = PREFECTURE_OPTIONS?.find((pre) => pre.value === code)?.label;
    return name;
  };

  const GetMetaInformations = (code) => {
    const metaData = CITY_META_DATA?.find((item) => item?.city == code);
    return metaData;
  };

  const [displayImage, setDisplayImage] = useState(true);

  return (
    <>
      <Head>
        <title>
          {t(GetMetaInformations(router.query.id)?.title)} -{" "}
          {t("Startup Ecosystem City")}
        </title>
        <meta
          key={GetPrefecture(id)}
          name="description"
          content={t(GetMetaInformations(router.query.id)?.description)}
        />
        <meta
          name="keywords"
          content={t(GetMetaInformations(router.query.id)?.keywords)}
        />
        <meta
          name="og:description"
          content={t(GetMetaInformations(router.query.id)?.description)}
        />
      </Head>

      <Wrapper>
        <CityTypography>{t("Startup Ecosystem City")}</CityTypography>
        <TitleTypography>
          {t(GetMetaInformations(router.query.id)?.pageHeading)}
        </TitleTypography>
        <ContentWrapper>{t(`description_${router.query.id}`)}</ContentWrapper>
        {displayImage && (
          <ImageWrapper>
            <DynamicCityImageWithNoSSR
              image={`/city/${router.query.id}.jpg`}
              onError={() => setDisplayImage(false)}
              className="img-size"
            />
            <ImageBackground />
          </ImageWrapper>
        )}
        <CityCard id={router.query.id} />
      </Wrapper>
      <TopCarousel fromCities initialData={props?.news} />

      <NoticeCarousel city={id} initialData={props?.featuredArticles} />
      <CalenderList city={id} initialData={props?.events} />
      <ArticleList
        city={id}
        initialData={props?.articles}
        initialTags={props?.initialTags}
      />
      <SupportCard city={router?.query?.id as string} />
      <LinkCardList city={id} initialData={props?.links} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { id } = context?.query;
  const articles = await GetArticleList({
    pageParam: {},
    queryKey: { 0: "getArticleList", 1: "0", 2: "1", 3: "12", 4: id },
  });

  const featuredArticles = await GetFeaturedArticle({
    queryKey: {
      0: "featuredArticle",
      1: id,
    },
  });

  const events = await GetTopEventsList({
    queryKey: {
      0: "events",
      1: id,
    },
  });

  const initialTags = await FetchTag({
    queryKey: {
      0: "",
      1: id,
    },
  });

  const news = await GetNewsList({
    pageParam: [],
    queryKey: {
      0: "",
      1: id,
    },
  });
  const links = await GetLinkList({
    pageParam: [],
    queryKey: {
      0: "",
      1: id,
    },
  });

  return {
    props: {
      ...(await serverSideTranslations(context?.locale, ["common"])),
      articles,
      featuredArticles,
      events,
      links,
      initialTags,
      news,
    },
  };
};
export default City;
